import React, { useState, useMemo } from 'react';
import { todayIST, formatIST } from '../utils/dates';
import { qualificationOf } from '../utils/qualification';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { leadActivityService, userService, leadService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Spinner } from './Spinner';
import { CheckCircleIcon, PencilIcon, ArrowPathIcon, ArrowRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const ROLE_LABELS = { super_admin: 'Super Admin', admin: 'Admin', dealer: 'Dealer', sales_executive: 'Sales Executive' };

const STATUS_LABELS = { new: 'New', in_progress: 'In Progress', follow_up: 'Follow Up', converted: 'Converted', lost: 'Lost' };
const STATUS_COLORS = {
  new:         'bg-blue-100 text-blue-700',
  in_progress: 'bg-indigo-100 text-indigo-700',
  follow_up:   'bg-orange-100 text-orange-700',
  converted:   'bg-green-100 text-green-700',
  lost:        'bg-red-100 text-red-700',
};
const STATUS_ORDER = ['new', 'in_progress', 'follow_up'];

// ── Hardcoded Activity Config ────────────────────────────────────────────────

const ACTIVITY_CONFIG = [
  {
    key: 'followup_lead',
    label: 'Follow-up Lead',
    subTypes: [
      { key: 'call_back',              label: 'Call Back' },
      { key: 'ask_to_call_later',      label: 'Ask to Call Later' },
      { key: 'ringing_not_responding', label: 'Ringing Not Responding' },
      { key: 'not_reachable',          label: 'Not Reachable' },
      { key: 'switch_off',             label: 'Switch Off' },
    ],
  },
  {
    key: 'face_to_face',
    label: 'Face to Face',
    subTypes: [
      { key: 'home_visit',           label: 'Home Visit' },
      { key: 'test_drive_schedule',  label: 'Test Drive Schedule' },
      { key: 'showroom_visit',       label: 'Showroom Visit' },
      { key: 'test_drive_completed', label: 'Test Drive Completed' },
    ],
  },
  {
    key: 'converted',
    label: 'Converted',
    subTypes: [
      { key: 'booked', label: 'Booked' },
      { key: 'retail', label: 'Retail' },
    ],
  },
  {
    key: 'lost',
    label: 'Lost',
    subTypes: [
      { key: 'lost_to_codealers',    label: 'Lost to Co-Dealers' },
      { key: 'lost_to_competitions', label: 'Lost to Competitors' },
      { key: 'not_purchased',        label: 'Not Purchase' },
      { key: 'existing',             label: 'Existing' },
    ],
  },
];

// Which primary activity keys are visible based on lead state + qualified choice
const getVisibleActivityKeys = (status, convertedSubType, isPermanentlyClosed, effectivelyQualified) => {
  if (isPermanentlyClosed) return [];
  if (status === 'lost') return ['reopen'];  // only re-open when lost
  if (status === 'converted' && convertedSubType === 'booked') return ['converted', 'lost'];
  // Not qualified (yet): follow-up is still allowed — a lead usually has to be
  // called before anyone can say whether it qualifies. Only face-to-face waits
  // for qualification.
  if (!effectivelyQualified) {
    return ['followup_lead', 'converted', 'lost'];
  }
  return ['followup_lead', 'face_to_face', 'converted', 'lost'];
};

// ── Component ────────────────────────────────────────────────────────────────

// ── Inline Forward Form ──────────────────────────────────────────────────────

const NEXT_ROLE = { super_admin: 'admin', admin: 'dealer', dealer: 'sales_executive' };

const InlineForwardForm = ({ lead, allUsers, user, onSuccess }) => {
  const isSuperAdmin = user?.role === 'super_admin';
  const nextRole = NEXT_ROLE[user?.role];

  const [open,                   setOpen]                   = useState(true);
  const [forwardToId,            setForwardToId]            = useState('');
  const [forwardRemark,          setForwardRemark]          = useState('');
  const [selectedForwardAdminId, setSelectedForwardAdminId] = useState('');

  const forwardableUsers = useMemo(() => {
    if (!nextRole || user?.role === 'sales_executive') return [];
    const myId = String(user._id || user.id || '');
    return allUsers.filter(u => {
      if (u.role !== nextRole) return false;
      if (isSuperAdmin) return true;
      const pid = typeof u.parentId === 'object' ? String(u.parentId?._id || '') : String(u.parentId || '');
      return pid === myId;
    });
  }, [allUsers, nextRole, isSuperAdmin, user]);

  const dealersUnderAdmin = useMemo(() => {
    if (!isSuperAdmin || !selectedForwardAdminId) return [];
    return allUsers.filter(u => {
      if (u.role !== 'dealer') return false;
      const pid = typeof u.parentId === 'object' ? String(u.parentId?._id || '') : String(u.parentId || '');
      return pid === selectedForwardAdminId;
    });
  }, [allUsers, isSuperAdmin, selectedForwardAdminId]);

  const forwardMutation = useMutation(
    (data) => leadService.forwardLead(lead._id, data),
    {
      onSuccess: () => {
        toast.success('Lead forwarded successfully');
        setOpen(false);
        setForwardToId('');
        setForwardRemark('');
        setSelectedForwardAdminId('');
        onSuccess();
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to forward lead'),
    }
  );

  const handleForward = () => {
    if (!forwardToId) {
      toast.error(`Please select a ${ROLE_LABELS[nextRole] || 'user'} to forward to.`);
      return;
    }
    forwardMutation.mutate({ forwardTo: forwardToId, remark: forwardRemark });
  };

  const inputCls = 'block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
  const labelCls = 'block text-xs text-gray-500 mb-1';

  return (
    <div className="rounded-lg border border-blue-200 overflow-hidden">
      {/* Toggle button */}
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full py-2 px-3 bg-white text-blue-600 text-sm font-semibold hover:bg-blue-50 transition-colors flex items-center justify-between"
      >
        <span className="flex items-center gap-2">
          <ArrowRightIcon className="h-4 w-4" />
          Forward Lead
        </span>
        <XMarkIcon className={`h-4 w-4 transition-transform ${open ? 'rotate-0' : 'rotate-45'}`} />
      </button>

      {/* Inline form — expands below the button */}
      {open && (
        <div className="border-t border-blue-100 bg-blue-50 p-3 space-y-3">
          {forwardableUsers.length === 0 ? (
            <p className="text-xs text-gray-500 text-center py-2">
              No {ROLE_LABELS[nextRole] || 'users'} available to forward to.
            </p>
          ) : (
            <>
              {isSuperAdmin ? (
                <>
                  <div>
                    <label className={labelCls}>Select Admin <span className="text-red-500">*</span></label>
                    <select className={inputCls} value={selectedForwardAdminId}
                      onChange={e => { setSelectedForwardAdminId(e.target.value); setForwardToId(''); }}>
                      <option value="">Select Admin</option>
                      {forwardableUsers.map(u => (
                        <option key={u._id} value={u._id}>{u.firstName} {u.lastName}</option>
                      ))}
                    </select>
                  </div>
                  {selectedForwardAdminId && (
                    <div>
                      <label className={labelCls}>Select Dealer <span className="text-red-500">*</span></label>
                      <select className={inputCls} value={forwardToId} onChange={e => setForwardToId(e.target.value)}>
                        <option value="">{dealersUnderAdmin.length === 0 ? 'No dealers under this admin' : 'Select Dealer'}</option>
                        {dealersUnderAdmin.map(u => (
                          <option key={u._id} value={u._id}>{u.firstName} {u.lastName}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              ) : (
                <div>
                  <label className={labelCls}>Forward To <span className="text-red-500">*</span></label>
                  <select className={inputCls} value={forwardToId} onChange={e => setForwardToId(e.target.value)}>
                    <option value="">Select {ROLE_LABELS[nextRole]}</option>
                    {forwardableUsers.map(u => (
                      <option key={u._id} value={u._id}>{u.firstName} {u.lastName}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className={labelCls}>Remark (optional)</label>
                <input type="text" className={inputCls} value={forwardRemark}
                  onChange={e => setForwardRemark(e.target.value)}
                  placeholder="Add a note for the receiving user" />
              </div>

              <button
                onClick={handleForward}
                disabled={forwardMutation.isLoading || !forwardToId || (isSuperAdmin && !selectedForwardAdminId)}
                className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors"
              >
                {forwardMutation.isLoading
                  ? <Spinner size="sm" />
                  : <><ArrowRightIcon className="h-4 w-4" />Confirm Forward</>
                }
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main Panel ───────────────────────────────────────────────────────────────

export const LeadActivityPanel = ({ lead, onDelete, deleteLoading }) => {
  const { user, hasPermission } = useAuth();
  const queryClient = useQueryClient();

  const [selectedTypeKey,    setSelectedTypeKey]    = useState('');
  const [reopenActivityKey,  setReopenActivityKey]  = useState(''); // activity type chosen after reopen
  const [selectedSubTypeKey, setSelectedSubTypeKey] = useState('');
  const [formValues,         setFormValues]         = useState({});
  const [updatedById,        setUpdatedById]        = useState(user._id || user.id);
  const [qualifiedChoice,    setQualifiedChoice]    = useState(''); // '' | 'yes' | 'no' — drives Activity Type filter

  // Fetch full lead detail (has populated assignmentHistory + new fields)
  const { data: leadDetailData } = useQuery(
    ['lead', lead._id],
    () => leadService.getLead(lead._id),
    { staleTime: 30_000, enabled: !!lead._id }
  );
  const leadDetail = leadDetailData?.data?.data?.lead || leadDetailData?.data?.lead || lead;

  const currentStatus       = leadDetail.status || lead.status || 'new';
  const isPermanentlyClosed = leadDetail.isPermanentlyClosed || false;
  const convertedSubType    = leadDetail.convertedSubType    || null;
  const qualificationStatus = leadDetail.qualificationStatus || lead.qualificationStatus || 'pending';
  const leadCreatedAt       = leadDetail.createdAt || lead.createdAt;

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: activitiesData, isLoading: activitiesLoading } = useQuery(
    ['leadActivities', lead._id],
    () => leadActivityService.getLeadActivities(lead._id),
    { staleTime: 30_000 }
  );

  const { data: usersData } = useQuery(
    'assignableUsers',
    userService.getAssignableUsers,
    { staleTime: 5 * 60 * 1000 }
  );

  // ── Derived ──────────────────────────────────────────────────────────────

  const activities = (() => {
    const d = activitiesData?.data;
    return d?.data?.activities || d?.activities || [];
  })();

  const allUsers     = usersData?.data?.data?.users || usersData?.data?.users || [];
  const latestActivity = activities[0];

  // Effectively qualified = persisted as qualified, OR user picked Yes in this form,
  // OR a lead from before qualification existed that's already past 'new'
  // (utils/qualification — the same rule as the leads list and the backend).
  const persistedQualified  = qualificationStatus === 'qualified';
  const legacyQualified     = qualificationStatus === 'pending'
    && qualificationOf({ qualificationStatus, status: currentStatus, createdAt: leadCreatedAt }) === 'qualified';
  const effectivelyQualified = persistedQualified || legacyQualified || qualifiedChoice === 'yes';
  // Show the Qualified Type dropdown only while the lead hasn't been persisted as qualified yet
  const showQualifiedDropdown = !persistedQualified && !legacyQualified && currentStatus !== 'lost' && !isPermanentlyClosed;

  const visibleActivityKeys = getVisibleActivityKeys(currentStatus, convertedSubType, isPermanentlyClosed, effectivelyQualified);
  const visibleActivities   = ACTIVITY_CONFIG.filter(a => visibleActivityKeys.includes(a.key));

  // When in reopen flow, the actual activity type is reopenActivityKey
  const effectiveTypeKey    = selectedTypeKey === 'reopen' ? reopenActivityKey : selectedTypeKey;
  const selectedTypeConfig  = ACTIVITY_CONFIG.find(a => a.key === effectiveTypeKey);
  const subTypes = (() => {
    const all = selectedTypeConfig?.subTypes || [];
    // If lead is already converted+booked, hide 'booked' — only show 'retail'
    if (effectiveTypeKey === 'converted' && convertedSubType === 'booked') {
      return all.filter(s => s.key !== 'booked');
    }
    return all;
  })();

  // Status section — forward-only manual buttons (for new/in_progress states)
  const currentStatusIdx = STATUS_ORDER.indexOf(currentStatus);
  const allowedStatuses  = currentStatusIdx >= 0 ? STATUS_ORDER.slice(currentStatusIdx + 1) : [];

  // ── Mutations ────────────────────────────────────────────────────────────

  const saveActivityMutation = useMutation(leadActivityService.createLeadActivity, {
    onSuccess: () => {
      toast.success('Activity saved');
      queryClient.invalidateQueries(['leadActivities', lead._id]);
      queryClient.invalidateQueries(['lead', lead._id]);
      queryClient.invalidateQueries('leads');
      queryClient.invalidateQueries('leadTabStats');   // the tab counts, with the list
      setSelectedTypeKey('');
      setReopenActivityKey('');
      setSelectedSubTypeKey('');
      setFormValues({});
      setQualifiedChoice('');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to save activity');
    },
  });

  const updateStatusMutation = useMutation(
    (newStatus) => leadService.updateLead(lead._id, { status: newStatus }),
    {
      onSuccess: (_, newStatus) => {
        toast.success(`Status updated to "${STATUS_LABELS[newStatus]}"`);
        queryClient.invalidateQueries(['lead', lead._id]);
        queryClient.invalidateQueries('leads');
        queryClient.invalidateQueries('leadTabStats');   // the tab counts, with the list
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to update status');
      },
    }
  );

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleTypeChange = (key) => {
    setSelectedTypeKey(key);
    setReopenActivityKey('');
    setSelectedSubTypeKey('');
    setFormValues({});
  };

  const handleSubTypeChange = (key) => {
    setSelectedSubTypeKey(key);
    setFormValues({});
  };

  const setField = (field, value) =>
    setFormValues(p => ({ ...p, [field]: value }));

  const validate = () => {
    if (!selectedSubTypeKey) { toast.error('Please select a sub-type'); return false; }
    // customDate required for all except converted-booked/retail and lost sub-types
    const noDateTypes = ['booked', 'retail', 'lost_to_codealers', 'lost_to_competitions', 'not_purchased', 'existing'];
    if (!noDateTypes.includes(selectedSubTypeKey) && !formValues.customDate) {
      toast.error('Custom date is required'); return false;
    }
    // Follow-up activities cannot be scheduled in the past
    if (effectiveTypeKey === 'followup_lead' && formValues.customDate) {
      const todayLocal = todayIST();   // India day
      if (formValues.customDate < todayLocal) {
        toast.error('Follow-up date cannot be in the past'); return false;
      }
    }
    if (!formValues.remark?.trim()) { toast.error('Remark is required'); return false; }
    // test_drive_completed requires a result selection
    if (selectedSubTypeKey === 'test_drive_completed' && !formValues.testDriveResult) {
      toast.error('Please select Ask to Call Back or Booked'); return false;
    }
    // lost sub-type validations
    if (selectedSubTypeKey === 'lost_to_codealers'    && !formValues.lostReason)          { toast.error('Please select a reason'); return false; }
    if (selectedSubTypeKey === 'lost_to_competitions' && (!formValues.competitorBrand || !formValues.competitorModel || !formValues.lostCompetitorReason)) {
      toast.error('Competitor brand, model and reason are required'); return false;
    }
    if (selectedSubTypeKey === 'not_purchased' && !formValues.notPurchaseReason) { toast.error('Please select a reason'); return false; }
    if (selectedSubTypeKey === 'existing' && !formValues.existingDate) { toast.error('Please select an existing date'); return false; }
    return true;
  };

  const buildPayload = () => {
    const typeConfig    = ACTIVITY_CONFIG.find(a => a.key === effectiveTypeKey);
    const subTypeConfig = typeConfig?.subTypes.find(s => s.key === selectedSubTypeKey);
    const { customDate, remark, ...rest } = formValues;
    const metadata = { ...rest };
    return {
      leadId:            lead._id,
      activityTypeKey:   effectiveTypeKey,
      activityTypeName:  typeConfig?.label   || effectiveTypeKey,
      subTypeKey:        selectedSubTypeKey,
      subTypeName:       subTypeConfig?.label || selectedSubTypeKey,
      customDate:        customDate || null,
      remark:            remark || '',
      status:            'saved',
      metadata,
      updatedByOverride: updatedById,
      // If the user chose Yes/No in the Qualified Type dropdown, persist it
      qualifiedChoice:   qualifiedChoice || undefined,
    };
  };

  const reopenMutationForSave = useMutation(
    (data) => leadService.reopenLead(lead._id, data),
    {
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to reopen lead');
      },
    }
  );

  const handleSave = async () => {
    if (!validate()) return;
    // If in reopen flow: reopen lead first, then save activity
    if (selectedTypeKey === 'reopen') {
      try {
        await reopenMutationForSave.mutateAsync({ remark: formValues.remark || '' });
      } catch {
        return; // reopen failed, don't save activity
      }
    }
    saveActivityMutation.mutate(buildPayload());
  };

  // ── Field renderers (called as functions, not JSX components, to avoid remount on re-render) ──

  const renderTextField = (field, label, placeholder = '') => (
    <div key={field}>
      <label className="block text-sm font-medium text-gray-600 mb-1.5">{label}</label>
      <input type="text" value={formValues[field] || ''} onChange={e => setField(field, e.target.value)}
        className="input" placeholder={placeholder} />
    </div>
  );

  const renderDateField = (field, label, opts = {}) => (
    <div key={field}>
      <label className="block text-sm font-medium text-gray-600 mb-1.5">{label}</label>
      <input
        type="date"
        value={formValues[field] || ''}
        onChange={e => setField(field, e.target.value)}
        min={opts.min}
        className="input"
      />
    </div>
  );

  const todayStr = todayIST();   // earliest follow-up date: today in India

  const renderSelectField = (field, label, options) => (
    <div key={field}>
      <label className="block text-sm font-medium text-gray-600 mb-1.5">{label}</label>
      <select value={formValues[field] || ''} onChange={e => setField(field, e.target.value)} className="input">
        <option value="">Select {label}</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );

  const renderRemarkField = () => (
    <div key="remark">
      <label className="block text-sm font-medium text-gray-600 mb-1.5">Remark <span className="text-red-500">*</span></label>
      <textarea rows={3} value={formValues.remark || ''} onChange={e => setField('remark', e.target.value)}
        className="input resize-none" placeholder="Enter remarks..." />
    </div>
  );

  const renderUpdatedByField = () => (
    <div key="updatedBy">
      <label className="block text-sm font-medium text-gray-600 mb-1.5">Updated By</label>
      <select value={updatedById} onChange={e => setUpdatedById(e.target.value)} className="input">
        <option value={user._id || user.id}>{user.firstName} {user.lastName} (You)</option>
        {allUsers
          .filter(u => u.role !== 'super_admin' && String(u._id) !== String(user._id || user.id))
          .map(u => (
            <option key={u._id} value={u._id}>{u.firstName} {u.lastName}</option>
          ))}
      </select>
    </div>
  );

  const renderDynamicFields = () => {
    if (!selectedSubTypeKey) return null;

    // ── Follow-up Lead sub-types (all same fields) ──
    if (effectiveTypeKey === 'followup_lead') {
      return (
        <>
          {renderDateField('customDate', 'Custom Date *', { min: todayStr })}
          {renderUpdatedByField()}
          {renderRemarkField()}
        </>
      );
    }

    // ── Face to Face ──
    if (effectiveTypeKey === 'face_to_face') {
      if (selectedSubTypeKey === 'home_visit') return (
        <>
          {renderDateField('customDate', 'Custom Date *')}
          {renderTextField('address', 'Address')}
          {renderUpdatedByField()}
          {renderRemarkField()}
        </>
      );
      if (selectedSubTypeKey === 'test_drive_schedule') return (
        <>
          {renderDateField('customDate', 'Custom Date *')}
          {renderTextField('location', 'Location')}
          {renderUpdatedByField()}
          {renderRemarkField()}
        </>
      );
      if (selectedSubTypeKey === 'showroom_visit') return (
        <>
          {renderDateField('customDate', 'Custom Date *')}
          {renderUpdatedByField()}
          {renderRemarkField()}
        </>
      );
      if (selectedSubTypeKey === 'test_drive_completed') return (
        <>
          {renderSelectField('testDriveResult', 'Result *', [
            { value: 'call_back', label: 'Ask to Call Back' },
            { value: 'booked',    label: 'Booked' },
          ])}
          {formValues.testDriveResult === 'booked' && (
            <div key="booked-warning" className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 font-medium">
              This will permanently close the lead as Booked.
            </div>
          )}
          {renderDateField('customDate', 'Custom Date *')}
          {renderUpdatedByField()}
          {renderRemarkField()}
        </>
      );
    }

    // ── Converted ──
    if (effectiveTypeKey === 'converted') {
      if (selectedSubTypeKey === 'booked') return (
        <>
          {renderTextField('customerName',  'Customer Name')}
          {renderTextField('customerPhone', 'Phone Number')}
          {renderTextField('bookingModel',  'Model')}
          {renderDateField('retailDate',    'Retail Date')}
          {renderDateField('bookingDate',   'Booking Date')}
          {renderUpdatedByField()}
          {renderRemarkField()}
        </>
      );
      if (selectedSubTypeKey === 'retail') return (
        <>
          <div key="retail-warning" className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 font-medium">
            This will permanently close the lead as Retail.
          </div>
          {renderUpdatedByField()}
          {renderRemarkField()}
        </>
      );
    }

    // ── Lost ──
    if (effectiveTypeKey === 'lost') {
      if (selectedSubTypeKey === 'lost_to_codealers') return (
        <>
          {renderSelectField('lostReason', 'Reason *', [
            { value: 'discount',     label: 'Discount' },
            { value: 'stock',        label: 'Stock' },
            { value: 'distance',     label: 'Distance' },
            { value: 'non_followup', label: 'Non Follow-up' },
          ])}
          {renderUpdatedByField()}
          {renderRemarkField()}
        </>
      );
      if (selectedSubTypeKey === 'lost_to_competitions') return (
        <>
          {renderTextField('competitorBrand', 'Competitor Brand *')}
          {renderTextField('competitorModel', 'Competitor Model *')}
          {renderSelectField('lostCompetitorReason', 'Reason *', [
            { value: 'product_related',       label: 'Product Related' },
            { value: 'finance_issue',         label: 'Finance Issue' },
            { value: 'poor_dealer_response',  label: 'Poor Dealer Response' },
            { value: 'purchased_ev',          label: 'Purchased Electric Vehicle' },
            { value: 'family_personal',       label: 'Family or Personal Choice' },
            { value: 'better_price',          label: 'Better Price or Offers' },
            { value: 'better_features',       label: 'Better Features' },
            { value: 'better_mileage',        label: 'Better Mileage' },
            { value: 'better_after_sale',     label: 'Better After Sale Service' },
            { value: 'low_maintenance',       label: 'Low Maintenance' },
            { value: 'out_of_budget',         label: 'Out of Budget' },
            { value: 'purchased_other_brand', label: 'Purchased Other Brand' },
          ])}
          {renderUpdatedByField()}
          {renderRemarkField()}
        </>
      );
      if (selectedSubTypeKey === 'not_purchased') return (
        <>
          {renderSelectField('notPurchaseReason', 'Reason *', [
            { value: 'plan_postponed',   label: 'Plan Postponed' },
            { value: 'budget_issue',     label: 'Budget Issue' },
            { value: 'not_interested',   label: 'Not Interested' },
            { value: 'already_purchase', label: 'Already Purchased' },
            { value: 'wrong_number',     label: 'Wrong Number' },
            { value: 'not_connected',    label: 'Not Connected' },
            { value: 'oft',              label: 'OFT' },
          ])}
          {renderUpdatedByField()}
          {renderRemarkField()}
        </>
      );
      if (selectedSubTypeKey === 'existing') return (
        <>
          {renderDateField('existingDate', 'Select Existing Date *')}
          {renderUpdatedByField()}
          {renderRemarkField()}
        </>
      );
    }

    return null;
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4" onClick={e => e.stopPropagation()}>

      {/* ── Lead Status ── */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <h4 className="text-base font-semibold text-gray-900 mb-3 pb-3 border-b border-gray-100">Lead Status</h4>
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${STATUS_COLORS[currentStatus] || 'bg-gray-100 text-gray-700'}`}>
            {STATUS_LABELS[currentStatus] || currentStatus}
            {isPermanentlyClosed && (
              <span className="ml-1.5 text-xs opacity-75">
                ({convertedSubType === 'retail' ? 'Retail' : 'Closed'})
              </span>
            )}
          </span>
          {/* Qualification badge */}
          {(() => {
            const effectivelyQualified = persistedQualified || legacyQualified;
            const tone = effectivelyQualified
              ? 'bg-emerald-100 text-emerald-700'
              : qualificationStatus === 'not_qualified'
                ? 'bg-amber-100 text-amber-700'
                : 'bg-gray-100 text-gray-600';
            const label = effectivelyQualified
              ? '✓ Qualified'
              : qualificationStatus === 'not_qualified'
                ? 'Not Qualified'
                : 'Qualification: Pending';
            return (
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${tone}`}>
                {label}
              </span>
            );
          })()}
          {allowedStatuses.length > 0 && hasPermission('leads', 'update') && !isPermanentlyClosed && (
            <>
              <span className="text-gray-300 text-sm">→ Move to:</span>
              {allowedStatuses.map(s => (
                <button key={s} onClick={() => updateStatusMutation.mutate(s)}
                  disabled={updateStatusMutation.isLoading}
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border-2 border-dashed transition-colors disabled:opacity-40 ${STATUS_COLORS[s]} hover:border-solid`}>
                  {updateStatusMutation.isLoading ? <Spinner size="sm" /> : STATUS_LABELS[s]}
                </button>
              ))}
            </>
          )}
          {isPermanentlyClosed && (
            <span className="text-xs text-gray-400">Lead is permanently closed</span>
          )}
        </div>
        {currentStatus === 'lost' && (
          <span className="text-xs text-gray-400 mt-1 block">Use the activity form below to re-open this lead</span>
        )}
      </div>

      {/* ── Lead Activity Form ── */}
      {visibleActivityKeys.length > 0 && hasPermission('leads', 'update') && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h4 className="text-base font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-100">Lead Activity</h4>
          <div className="space-y-4">

            {/* Qualified Type dropdown — only when lead is not yet persisted as qualified */}
            {showQualifiedDropdown && (
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1.5">
                  Qualified Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={qualifiedChoice}
                  onChange={e => {
                    setQualifiedChoice(e.target.value);
                    // If user flips the qualified choice, reset activity selection
                    setSelectedTypeKey('');
                    setReopenActivityKey('');
                    setSelectedSubTypeKey('');
                    setFormValues({});
                  }}
                  className="input"
                >
                  <option value="">Select Qualified Type</option>
                  <option value="yes">Yes — Qualified</option>
                  <option value="no">No — Not Qualified</option>
                </select>
              </div>
            )}

            {/* Primary dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1.5">Activity Type</label>
              <select
                value={selectedTypeKey}
                onChange={e => handleTypeChange(e.target.value)}
                className="input"
                disabled={showQualifiedDropdown && !qualifiedChoice}
              >
                <option value="">Select Activity</option>
                {visibleActivities.map(a => (
                  <option key={a.key} value={a.key}>{a.label}</option>
                ))}
                {visibleActivityKeys.includes('reopen') && (
                  <option value="reopen">Re-open Enquiry</option>
                )}
              </select>
            </div>

            {/* Re-open flow: show all 4 activity types as second dropdown */}
            {selectedTypeKey === 'reopen' && (
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1.5">Select Activity After Re-open</label>
                <select value={reopenActivityKey}
                  onChange={e => { setReopenActivityKey(e.target.value); setSelectedSubTypeKey(''); setFormValues({}); }}
                  className="input">
                  <option value="">Select Activity</option>
                  {ACTIVITY_CONFIG.map(a => (
                    <option key={a.key} value={a.key}>{a.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Sub-type dropdown */}
            {effectiveTypeKey && (
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1.5">Sub Type</label>
                <select value={selectedSubTypeKey} onChange={e => handleSubTypeChange(e.target.value)} className="input">
                  <option value="">Select Sub Type</option>
                  {subTypes.map(s => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Dynamic fields */}
            {selectedSubTypeKey && (
              <div className="space-y-3 pt-1">
                {renderDynamicFields()}
              </div>
            )}

            {/* Save button */}
            {selectedSubTypeKey && (
              <div className="pt-2">
                <button onClick={handleSave} disabled={saveActivityMutation.isLoading}
                  className="w-full py-2.5 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 active:bg-primary-800 disabled:opacity-40 transition-colors shadow-sm">
                  {saveActivityMutation.isLoading ? <Spinner size="sm" /> : selectedTypeKey === 'reopen' ? 'Re-open & Save Activity' : 'Save Activity'}
                </button>
              </div>
            )}

          </div>
        </div>
      )}


      {/* ── Assignment History ── */}
      {false && leadDetail.assignmentHistory?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h4 className="text-base font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-100 flex items-center gap-2">
            Assignment History
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-600">
              {leadDetail.assignmentHistory.length}
            </span>
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 pr-3 text-xs font-semibold text-gray-500 whitespace-nowrap">#</th>
                  <th className="text-left py-2 pr-3 text-xs font-semibold text-gray-500 whitespace-nowrap">Action</th>
                  <th className="text-left py-2 pr-3 text-xs font-semibold text-gray-500 whitespace-nowrap">From</th>
                  <th className="text-left py-2 pr-3 text-xs font-semibold text-gray-500 whitespace-nowrap">To</th>
                  <th className="text-left py-2 pr-3 text-xs font-semibold text-gray-500 whitespace-nowrap">Remark</th>
                  <th className="text-left py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {leadDetail.assignmentHistory.map((h, idx) => {
                  const fromName = h.fromUser?.firstName ? `${h.fromUser.firstName} ${h.fromUser.lastName}` : 'System';
                  const toName   = h.toUser?.firstName   ? `${h.toUser.firstName} ${h.toUser.lastName}`     : 'Unknown';
                  const actionBadge = h.action === 'created'
                    ? 'bg-green-100 text-green-700'
                    : h.action === 'reopened'
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-blue-100 text-blue-700';
                  return (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="py-2.5 pr-3 text-gray-400 text-xs">{idx + 1}</td>
                      <td className="py-2.5 pr-3">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold capitalize ${actionBadge}`}>
                          {h.action || 'forwarded'}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3 text-gray-700 whitespace-nowrap">
                        {fromName}
                        {h.fromRole && <div className="text-xs text-gray-400">{ROLE_LABELS[h.fromRole] || h.fromRole}</div>}
                      </td>
                      <td className="py-2.5 pr-3 text-gray-700 whitespace-nowrap">
                        {toName}
                        {h.toRole && <div className="text-xs text-gray-400">{ROLE_LABELS[h.toRole] || h.toRole}</div>}
                      </td>
                      <td className="py-2.5 pr-3 text-gray-500 text-xs max-w-[120px] truncate">{h.remark || '—'}</td>
                      <td className="py-2.5 text-gray-500 text-xs whitespace-nowrap">
                        {h.assignedAt ? formatIST(h.assignedAt, 'dd MMM yy') : '—'}
                        {h.assignedAt && <div className="text-gray-400">{formatIST(h.assignedAt, 'HH:mm')}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Quick Actions ── */}
      {user?.role !== 'sales_executive' && (hasPermission('leads', 'update') || hasPermission('leads', 'delete')) && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h4 className="text-base font-semibold text-gray-900 mb-3 pb-3 border-b border-gray-100">Quick Actions</h4>
          <div className="space-y-2">
            {hasPermission('leads', 'update') && (
              <Link
                to={`/leads/${lead._id}/edit`}
                className="w-full py-2 rounded-lg border border-primary-200 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors flex items-center justify-center gap-2"
                onClick={e => e.stopPropagation()}
              >
                <PencilIcon className="h-4 w-4" />
                Edit Lead
              </Link>
            )}
            {hasPermission('leads', 'update') && NEXT_ROLE[user?.role] && (
              <InlineForwardForm
                lead={lead}
                allUsers={allUsers}
                user={user}
                onSuccess={() => {
                  queryClient.invalidateQueries(['lead', lead._id]);
                  queryClient.invalidateQueries('leads');
                  queryClient.invalidateQueries('leadTabStats');   // the tab counts, with the list
                }}
              />
            )}
            {hasPermission('leads', 'delete') && onDelete && (
              <button
                onClick={() => { if (window.confirm('Delete this lead? It moves to the Recycle Bin and can be restored for 7 days.')) onDelete(lead._id); }}
                disabled={deleteLoading}
                className="w-full py-2 rounded-lg border border-red-200 text-red-500 text-sm font-semibold hover:bg-red-50 disabled:opacity-40 transition-colors"
              >
                Delete Lead
              </button>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

// ── Reopen Button (standalone — shown in Lead Status section) ────────────────

const ReopenButton = ({ leadId, queryClient }) => {
  const [showRemark, setShowRemark] = useState(false);
  const [remark, setRemark]         = useState('');

  const reopenMutation = useMutation(
    (data) => leadService.reopenLead(leadId, data),
    {
      onSuccess: () => {
        toast.success('Lead reopened successfully');
        queryClient.invalidateQueries(['lead', leadId]);
        queryClient.invalidateQueries(['leadActivities', leadId]);
        queryClient.invalidateQueries('leads');
        queryClient.invalidateQueries('leadTabStats');   // the tab counts, with the list
        setShowRemark(false);
        setRemark('');
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to reopen lead');
      },
    }
  );

  if (!showRemark) {
    return (
      <button onClick={e => { e.stopPropagation(); setShowRemark(true); }}
        className="w-full py-2 rounded-lg border border-primary-200 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors flex items-center justify-center gap-2">
        <ArrowPathIcon className="h-4 w-4" />
        Reopen Lead
      </button>
    );
  }

  return (
    <div className="space-y-2" onClick={e => e.stopPropagation()}>
      <textarea rows={2} value={remark} onChange={e => setRemark(e.target.value)}
        className="input text-sm resize-none" placeholder="Reason for reopening (optional)..." />
      <div className="flex gap-2">
        <button onClick={() => reopenMutation.mutate({ remark })} disabled={reopenMutation.isLoading}
          className="flex-1 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-40 transition-colors">
          {reopenMutation.isLoading ? <Spinner size="sm" /> : 'Confirm Reopen'}
        </button>
        <button onClick={() => setShowRemark(false)}
          className="px-3 py-2 rounded-lg border border-gray-200 text-gray-500 text-sm hover:bg-gray-50 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
};

// ── Reopen Inline (inside Activity Form when "Re-open Enquiry" selected) ─────

const ReopenInline = ({ leadId, queryClient, onDone }) => {
  const [remark, setRemark] = useState('');

  const reopenMutation = useMutation(
    (data) => leadService.reopenLead(leadId, data),
    {
      onSuccess: () => {
        toast.success('Lead reopened successfully');
        queryClient.invalidateQueries(['lead', leadId]);
        queryClient.invalidateQueries(['leadActivities', leadId]);
        queryClient.invalidateQueries('leads');
        queryClient.invalidateQueries('leadTabStats');   // the tab counts, with the list
        onDone();
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to reopen lead');
      },
    }
  );

  return (
    <div className="space-y-3 pt-1">
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1.5">Remark</label>
        <textarea rows={3} value={remark} onChange={e => setRemark(e.target.value)}
          className="input resize-none" placeholder="Reason for reopening (optional)..." />
      </div>
      <button onClick={() => reopenMutation.mutate({ remark })} disabled={reopenMutation.isLoading}
        className="w-full py-2.5 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-40 transition-colors shadow-sm flex items-center justify-center gap-2">
        <ArrowPathIcon className="h-4 w-4" />
        {reopenMutation.isLoading ? <Spinner size="sm" /> : 'Confirm Re-open Enquiry'}
      </button>
    </div>
  );
};
